import { inject, Injectable } from '@angular/core';
import { AuthService } from "@core/services/auth.service";
import { UserRecord } from '@models/organisations/user-record';
import BaseApiService from "@shared/services/base/base-api.service";
import { map, of, switchMap, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class UsersApiService extends BaseApiService {
  private authService = inject(AuthService);

  private assignableUsersCache: UserRecord[] | null = null;

  getUsers() {
    return this.api.get<UserRecord[]>(`${ this.apiUrl }/users`)
      .pipe(
        map((users) => users.map((user) => new UserRecord(user)))
      );
  }

  getAssignableUsers() {
    const allowedRoles = ['Admin', 'Manager'];
    return this.getUsers()
      .pipe(
        map((users) => {
          return users.filter((user) => {
            return user.id && user.roles.some((role) => allowedRoles.includes(role));
          });
        })
      );
  }

  getAssignableUsersCached() {
    if (this.assignableUsersCache) {
      return of(this.assignableUsersCache);
    }

    return this.getAssignableUsers()
      .pipe(
        tap(assignableUsers => {
          this.assignableUsersCache = assignableUsers;
        })
      );
  }

  saveUser(user: UserRecord) {
    return this.api.post(`${ this.apiUrl }/users`, user)
      .pipe(
        switchMap((response) => this.authService.updateUserProfile()
          .pipe(
            map(() => response)
          ))
      );
  }

  deleteUser(externalId: string) {
    return this.api.delete(`${ this.apiUrl }/users/${ externalId }`)
      .pipe(
        switchMap((response) => this.authService.updateUserProfile()
          .pipe(
            map(() => response)
          ))
      );
  }

  triggerPasswordReset(externalId: string) {
    return this.api.post(`${ this.apiUrl }/users/${ externalId }/reset-password`, null);
  }
}
